import React, { useState } from 'react';

import { link } from '@aline/routes';
import { Menu } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ConfirmationDialog,
  DropdownSelectButton,
  OptionsMenuItem,
  useRequestReportExport,
} from 'src/components';
import {
  ExportExtractionResultsWarningModal,
  useExtractionReportWarning,
} from 'src/components/repository/ExportExtractionResultsWarningModal';
import { Apollo } from 'src/gql/generated';
import { useDeleteReports } from 'src/hooks';

interface ReportActionsProps {
  reportId: UUID;
  reportName: string;
}

export function ReportActions({ reportId }: ReportActionsProps): JSX.Element {
  const navigate = useNavigate();
  const { deleteReports } = useDeleteReports();
  const [open, setOpen] = useState<boolean>(false);
  const { getAreExtractionsStale } = useExtractionReportWarning();
  const [dialogOpen, setDialogOpen] = useState<'delete' | 'extraction-warning' | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { requestReportExport } = useRequestReportExport();

  async function handleDeleteConfirm(): Promise<void> {
    await deleteReports([reportId]);
    navigate(link.ReportIndex());
  }

  return (
    <>
      <DropdownSelectButton
        ref={setAnchorEl}
        label="Actions"
        selected={open}
        variant="outlined"
        onClick={() => setOpen(!open)}
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        open={open}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        variant="menu"
        onClose={() => setOpen(false)}
      >
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);

            if (getAreExtractionsStale()) setDialogOpen('extraction-warning');
            else requestReportExport({ format: Apollo.ExportFormat.Csv, reportId });
          }}
          text="Export report (CSV)"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);

            if (getAreExtractionsStale()) setDialogOpen('extraction-warning');
            else requestReportExport({ format: Apollo.ExportFormat.Xlsx, reportId });
          }}
          text="Export report (XLSX)"
        />
        <OptionsMenuItem
          clickHandler={() => {
            setOpen(false);
            setDialogOpen('delete');
          }}
          text="Delete"
        />
      </Menu>
      <ConfirmationDialog
        message="Are you sure you want to delete the report?  This action cannot be undone."
        open={dialogOpen === 'delete'}
        title="Delete report"
        onClickPrimary={handleDeleteConfirm}
        onClose={() => setDialogOpen(null)}
      />
      <ExportExtractionResultsWarningModal
        open={dialogOpen === 'extraction-warning'}
        reportId={reportId}
        onClose={() => setDialogOpen(null)}
      />
    </>
  );
}
